import exclamationCircleIcon from "@tabler/icons/outline/exclamation-circle.svg";
import externalLinkIcon from "@tabler/icons/outline/external-link.svg";
import React, { useEffect, useState } from "react";
import { useIntl, defineMessages } from "react-intl";

import { Button, HStack, Icon, Stack, Text } from "@/components/ui";
import { useModalContext } from "@/contexts/modal-context";
import { cn } from "@/utils/cn";

import type {
  EmbeddedAnnouncementState,
  EmbeddedAnnouncement,
} from "@/schemas/embedded-announcement";

const messages = defineMessages({
  liveLabel: {
    id: "embedded_announcement.live",
    defaultMessage: "Live",
  },
  paidAdLabel: {
    id: "embedded_announcement.paid_ad",
    defaultMessage: "This is a paid ad from {advertiser}.",
  },
  airing: {
    id: "embedded_announcement.airing",
    defaultMessage: "Airing {date}",
  },
});

interface EmbeddedAnnouncementProps {
  announcement: EmbeddedAnnouncement;
}

// Don't schedule a state flip that's further away than a realistic session —
// the app won't stay open long enough for the timer to fire, so it's wasted.
const MAX_SCHEDULE_AHEAD = 60 * 60 * 1000; // 1h

export const getEmbeddedAnnouncementState = (
  announcement: EmbeddedAnnouncement,
  now: Date = new Date(),
): EmbeddedAnnouncementState => {
  if (!announcement.starts_at || !announcement.ends_at) return "default";
  const start = new Date(announcement.starts_at);
  const end = new Date(announcement.ends_at);
  if (now < start) return "upcoming";
  if (now <= end) return "live";
  return "default";
};

const EmbeddedAnnouncement: React.FC<EmbeddedAnnouncementProps> = ({
  announcement,
}) => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  // Re-render at the next start/end boundary so the card flips
  // upcoming → live → default on its own while mounted.
  const [now, setNow] = useState(() => new Date());
  const state = getEmbeddedAnnouncementState(announcement, now);

  const isLive = state === "live";

  const ctaLabel = isLive
    ? (announcement.live_cta_label ?? announcement.cta_label)
    : announcement.cta_label;

  const ctaUrl = isLive
    ? (announcement.live_cta_url ?? announcement.cta_url)
    : announcement.cta_url;

  const upcomingChip =
    state === "upcoming" && announcement.starts_at
      ? intl.formatMessage(messages.airing, {
          date: intl.formatDate(new Date(announcement.starts_at), {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
        })
      : null;

  const handleCtaClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(ctaUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const { starts_at, ends_at } = announcement;
    if (!starts_at || !ends_at) return;

    const nextBoundary = [new Date(starts_at), new Date(ends_at)].find(
      (date) => date.getTime() > Date.now(),
    );
    if (!nextBoundary) return;

    const delay = nextBoundary.getTime() - Date.now();
    if (delay > MAX_SCHEDULE_AHEAD) return;

    const timer = window.setTimeout(() => setNow(new Date()), delay);
    return () => clearTimeout(timer);
  }, [announcement.starts_at, announcement.ends_at, now]);

  return (
    <Stack space={2}>
      <div
        className={cn(
          "rounded-xl border p-4",
          isLive
            ? "border-secondary-500 bg-secondary-500/8 dark:border-red-800 dark:bg-red-950/20"
            : "bg-white dark:bg-transparent",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack space={4}>
          {/* Header row */}
          <HStack space={3} alignItems="center">
            {announcement.logo ? (
              <img
                src={announcement.logo}
                alt={announcement.advertiser_name}
                className="size-10 shrink-0 rounded-full object-contain"
              />
            ) : (
              <div className="size-10 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />
            )}

            <Text size="md" weight="bold" className="flex-1 truncate">
              {announcement.advertiser_name}
              {` · `}
              <span className="font-medium">{announcement.headline}</span>
            </Text>

            {isLive && (
              <div className="mr-3 flex shrink-0 items-center gap-2">
                <Text theme="secondary" size="sm" className="font-semibold">
                  {intl.formatMessage(messages.liveLabel)}
                </Text>
              </div>
            )}

            {upcomingChip && (
              <Text size="sm" theme="muted" className="shrink-0">
                {upcomingChip}
              </Text>
            )}
          </HStack>

          {/* Description */}
          <Text>{announcement.description}</Text>

          {/* CTA button */}
          <Button
            variant="default"
            onClick={handleCtaClick}
            block
            className="flex! items-center!"
          >
            <span className="flex items-center justify-center gap-1 text-base font-medium">
              {ctaLabel}
              <Icon src={externalLinkIcon} className="size-4" />
            </span>
          </Button>
        </Stack>
      </div>

      {/* Attribution */}
      <HStack space={1} alignItems="center">
        <button
          onClick={(event) => {
            event.stopPropagation();
            openModal("AD_DISCLAMER");
          }}
        >
          <Icon src={exclamationCircleIcon} className="size-5 text-gray-600" />
        </button>
        <Text size="xs" theme="muted">
          {intl.formatMessage(messages.paidAdLabel, {
            advertiser: announcement.advertiser_name,
          })}
        </Text>
      </HStack>
    </Stack>
  );
};

export default EmbeddedAnnouncement;
